import {
  BOARD_SIZE,
  TOWER_KINDS,
  emptyLayout,
  idx,
  isCore,
  type Layout,
  type Tile,
  type TowerKind,
} from "@empire/core-sim";
import { z } from "zod";

/**
 * The shape a layout travels in (05 Data Model, `layout` entity).
 *
 * The simulation works on a DENSE board: 144 tiles, most of them empty. The
 * wire format is SPARSE: a gate, a list of the tiles that are not empty, and a
 * list of towers. That is not duplication for its own sake — a dense board is
 * about 4 kB of mostly "empty" on every save, and a defender on a phone pays
 * for each one.
 *
 * The risk of having two shapes is that they drift, so there is exactly one
 * pair of functions that converts between them, both live here where the client
 * and the server share them, and a test round-trips every scenario through both
 * directions. Neither side is allowed its own translation.
 */

export const SURFACES = ["road", "mud", "barricade"] as const;
export type Surface = (typeof SURFACES)[number];

/** [x, y] — the tile attackers enter on. */
export const WireCoord = z.tuple([z.number().int(), z.number().int()]);

/** [x, y, surface] for each tile of the road. */
export const WireRoadTile = z.tuple([
  z.number().int(),
  z.number().int(),
  z.enum(SURFACES),
]);

export const WireTower = z.object({
  type: z.enum(TOWER_KINDS as unknown as [TowerKind, ...TowerKind[]]),
  level: z.number().int().min(1).max(3),
  x: z.number().int(),
  y: z.number().int(),
});

export const LayoutPayload = z.object({
  gate: WireCoord,
  road: z.array(WireRoadTile).max(BOARD_SIZE * BOARD_SIZE),
  towers: z.array(WireTower).max(BOARD_SIZE * BOARD_SIZE),
  /** Tower SLOTS, including empty ones — a slot is a build site, not a tower. */
  slots: z.array(WireCoord).max(BOARD_SIZE * BOARD_SIZE),
  /**
   * Phase 0 only. In the real game these come from the village's buildings;
   * there are no buildings yet, so the editor carries them and the server
   * stores them alongside the layout.
   */
  wallLevel: z.number().int().min(0).max(10).default(0),
  engineersGuildLevel: z.number().int().min(0).max(20).default(0),
});
export type LayoutPayload = z.infer<typeof LayoutPayload>;

/** What the defender is allowed, echoed back so the client need not recompute. */
export const LayoutBudget = z.object({
  road: z.number().int(),
  mud: z.number().int(),
  barricade: z.number().int(),
  slots: z.number().int(),
});
export type LayoutBudget = z.infer<typeof LayoutBudget>;

export const LayoutValidation = z.object({
  valid: z.boolean(),
  errors: z.array(
    z.object({
      code: z.string(),
      message: z.string(),
      tiles: z.array(WireCoord),
    })
  ),
});
export type LayoutValidation = z.infer<typeof LayoutValidation>;

export const LayoutResponse = LayoutValidation.extend({
  layout: LayoutPayload,
  budget: LayoutBudget,
  /** Canonical hash, for the dominant-layout telemetry R-02 wants. */
  hash: z.string().optional(),
  savedAt: z.string().optional(),
  /**
   * What this village has already paid for tower upgrades (D-119), so the
   * editor can show what the next save will cost. Optional for older servers.
   */
  upgradesPaid: z
    .object({ wood: z.number().int(), clay: z.number().int(), iron: z.number().int(), grain: z.number().int() })
    .optional(),
  /** What this save charged, on a PUT. */
  charged: z
    .object({ wood: z.number().int(), clay: z.number().int(), iron: z.number().int(), grain: z.number().int() })
    .optional(),
});
export type LayoutResponse = z.infer<typeof LayoutResponse>;

const inBounds = (x: number, y: number): boolean =>
  Number.isInteger(x) && Number.isInteger(y) && x >= 0 && y >= 0 && x < BOARD_SIZE && y < BOARD_SIZE;

/**
 * Sparse payload -> the dense board the simulation runs on.
 *
 * Anything out of bounds or on the core is DROPPED rather than rejected. The
 * validator is the thing that says yes or no, and it says so in the defender's
 * language ("the road cannot fork"); a converter throwing first would replace
 * that with a shape complaint about a board they cannot see.
 */
export function toLayout(payload: LayoutPayload): Layout {
  const base = emptyLayout();
  const tiles: Tile[] = base.tiles.map((t) => ({ kind: t.kind }));

  for (const [x, y, surface] of payload.road) {
    if (!inBounds(x, y) || isCore(x, y)) continue;
    tiles[idx(x, y, base.size)] = { kind: surface };
  }

  for (const [x, y] of payload.slots) {
    if (!inBounds(x, y) || isCore(x, y)) continue;
    tiles[idx(x, y, base.size)] = { kind: "tower_slot" };
  }

  // Towers come last: a tower implies its slot, so it wins over both lists.
  for (const tower of payload.towers) {
    if (!inBounds(tower.x, tower.y) || isCore(tower.x, tower.y)) continue;
    tiles[idx(tower.x, tower.y, base.size)] = {
      kind: "tower_slot",
      tower: { kind: tower.type, level: tower.level },
    };
  }

  const [gx, gy] = payload.gate;
  return {
    ...base,
    tiles,
    gate: inBounds(gx, gy) ? { x: gx, y: gy } : base.gate,
    wallLevel: payload.wallLevel,
    engineersGuildLevel: payload.engineersGuildLevel,
  };
}

/** The dense board -> the sparse payload. The exact inverse of `toLayout`. */
export function fromLayout(layout: Layout): LayoutPayload {
  const road: LayoutPayload["road"] = [];
  const slots: LayoutPayload["slots"] = [];
  const towers: LayoutPayload["towers"] = [];

  for (let y = 0; y < layout.size; y++) {
    for (let x = 0; x < layout.size; x++) {
      const tile = layout.tiles[idx(x, y, layout.size)];
      if (!tile) continue;
      if (tile.kind === "road" || tile.kind === "mud" || tile.kind === "barricade") {
        road.push([x, y, tile.kind]);
      } else if (tile.kind === "tower_slot") {
        if (tile.tower) {
          towers.push({ type: tile.tower.kind, level: tile.tower.level, x, y });
        } else {
          slots.push([x, y]);
        }
      }
    }
  }

  return {
    gate: [layout.gate.x, layout.gate.y],
    road,
    slots,
    towers,
    wallLevel: layout.wallLevel,
    engineersGuildLevel: layout.engineersGuildLevel,
  };
}

/**
 * The defence every village starts with (playtest, 29 Sep 2026: "a village
 * must have a default road of defence, and a player cannot be without a valid
 * defence road at any time").
 *
 * A short switchback in from the west edge, with two Arrow towers and a
 * Brazier where it bends — legal at Wall 0 and Engineer's Guild 0, so it is
 * legal at every level a village can have. It is seeded when a village is
 * founded, and it is what "Default road" in the editor puts back. A test
 * checks it against `validateLayout` at every Wall and Guild level.
 */
export const DEFAULT_ROAD: readonly (readonly [number, number])[] = [
  [0, 3],
  [1, 3],
  [2, 3],
  [2, 4],
  [2, 5],
  [2, 6],
  [2, 7],
  [3, 7],
  [4, 7],
  [4, 6],
];

export const DEFAULT_TOWERS: readonly (readonly [number, number, TowerKind])[] = [
  [1, 4, "arrow"],
  [3, 6, "brazier"],
  [3, 4, "arrow"],
];

export function defaultLayout(levels: { wallLevel?: number; engineersGuildLevel?: number } = {}): Layout {
  return toLayout({
    gate: [DEFAULT_ROAD[0]![0], DEFAULT_ROAD[0]![1]],
    road: DEFAULT_ROAD.map(([x, y]) => [x, y, "road"] as [number, number, Surface]),
    slots: [],
    towers: DEFAULT_TOWERS.map(([x, y, type]) => ({ x, y, type, level: 1 })),
    wallLevel: levels.wallLevel ?? 0,
    engineersGuildLevel: levels.engineersGuildLevel ?? 0,
  });
}
